import { useEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import {
  Dialog,
  Dropdown,
  F0ActionBar,
  OneEmptyState,
} from '@factorialco/f0-react/dist/experimental'
import { F0TableOfContent } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Ellipsis, File, Question, Video } from '@factorialco/f0-react/icons/app'

import { useNavConfirmModal } from 'modules/core/hooks/useNavConfirmModal'
import i18n from 'modules/core/lib/i18n'

import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import { ContentItem } from 'modules/trainings/lib/content/ContentItem'
import { ContentModule } from 'modules/trainings/lib/content/Module'

type ModalState = { mode: 'closed' } | { mode: 'create' } | { mode: 'edit'; item: ContentItem }

type TocItem = {
  id: string
  label: string
  onClick: () => void
  children?: TocItem[]
}

interface BuilderProps {
  tocItems: TocItem[]
  selectedItem?: ContentItem
  trainingId: string
  hasUnsavedChanges?: boolean
  isThinking?: boolean
  onReorder: (orderedStructure: Array<{ id: string; children?: Array<{ id: string }> }>) => void
  onAddContent: (params: { contentItem: ContentItem }) => void
  onDeleteContent: (uuid: string) => void
  onContentChange: () => void
  onSave: () => void
  onDiscard: () => void
}

const ContentBuilder = ({
  tocItems,
  selectedItem,
  trainingId,
  hasUnsavedChanges = false,
  isThinking = false,
  onReorder,
  onAddContent,
  onDeleteContent,
  onContentChange,
  onSave,
  onDiscard,
}: BuilderProps) => {
  const [modalState, setModalState] = useState<ModalState>({ mode: 'closed' })
  const { NavConfirmModal, setNavigationBlocked } = useNavConfirmModal()

  // Block navigation when modal is open OR when there are unsaved changes
  useEffect(() => {
    const shouldBlock = modalState.mode !== 'closed' || hasUnsavedChanges
    setNavigationBlocked(shouldBlock)
  }, [modalState.mode, hasUnsavedChanges, setNavigationBlocked])
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  return (
    <>
      <Box
        border={{
          top: {
            width: 's1',
            style: 'solid',
            color: 'grey200',
          },
        }}
        flexDirection="row"
        position="absolute"
        style={{
          inset: '0',
          overflow: 'hidden',
        }}
      >
        <Box
          border={{
            right: {
              width: 's1',
              style: 'solid',
              color: 'grey200',
            },
          }}
          style={{
            width: '248px',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Box flexDirection="column" gap="s12" paddingTop="s12" width="full">
            <F0TableOfContent
              className="w-full"
              activeItem={selectedItem?.uuid}
              items={tocItems}
              title={i18n.t('trainings.content.builder.title')}
              sortable
              onReorder={(
                reorderedIds: Array<{ id: string; children?: Array<{ id: string }> }>
              ) => {
                onReorder(reorderedIds)
              }}
            />
            {tocItems.length !== 0 && (
              <Box alignSelf="flexStart" paddingX="s12">
                <Dropdown
                  label={i18n.t('trainings.content.builder.add')}
                  icon={Add}
                  items={[
                    {
                      label: i18n.t('trainings.content.builder.add_module'),
                      icon: Add,
                      onClick: () => {
                        onAddContent({ contentItem: contentRegistry.createDefaultItem('module') })
                      },
                    },
                    {
                      type: 'separator',
                    },
                    { type: 'label', text: 'In the module' },
                    {
                      label: i18n.t('trainings.content.builder.add_page'),
                      icon: File,
                      onClick: () => {
                        onAddContent({ contentItem: contentRegistry.createDefaultItem('page') })
                      },
                    },
                    {
                      label: i18n.t('trainings.content.builder.add_video'),
                      icon: Video,
                      onClick: () => {
                        onAddContent({ contentItem: contentRegistry.createDefaultItem('video') })
                      },
                    },
                    {
                      label: i18n.t('trainings.content.builder.add_quiz'),
                      icon: Question,
                      onClick: () => {
                        onAddContent({
                          contentItem: contentRegistry.createDefaultItem('quiz', trainingId),
                        })
                      },
                    },
                  ]}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          flexGrow
          position="relative"
          style={{
            width: 'calc(100% - 248px)',
          }}
        >
          {!selectedItem ? (
            <Box alignItems="center" justifyContent="center" height="full">
              <OneEmptyState
                emoji="📚"
                title={i18n.t('trainings.content.builder.empty.header')}
                description={i18n.t('trainings.content.builder.empty.description')}
                actions={[
                  {
                    icon: Add,
                    label: i18n.t('trainings.content.builder.empty.button'),
                    onClick: () =>
                      onAddContent({ contentItem: contentRegistry.createDefaultItem('module') }),
                    variant: 'outline',
                  },
                ]}
              />
            </Box>
          ) : (
            <Box flexDirection="column" height="full" overflowY="hidden">
              <Box
                flexDirection="row"
                justifyContent="spaceBetween"
                width="full"
                height="s64"
                flexNoShrink
              >
                <Box paddingTop="s16" paddingLeft="s12">
                  <ItemTag item={selectedItem} />
                </Box>
                <Box
                  flexDirection="row"
                  alignItems="flexStart"
                  gap="s12"
                  paddingTop="s16"
                  paddingRight="s24"
                >
                  {selectedItem instanceof ContentModule && (
                    <Dropdown
                      icon={Add}
                      items={[
                        {
                          label: i18n.t('trainings.content.builder.add_page'),
                          icon: File,
                          onClick: () => {
                            onAddContent({ contentItem: contentRegistry.createDefaultItem('page') })
                          },
                        },
                        {
                          label: i18n.t('trainings.content.builder.add_video'),
                          icon: Video,
                          onClick: () => {
                            onAddContent({
                              contentItem: contentRegistry.createDefaultItem('video'),
                            })
                          },
                        },
                        {
                          label: i18n.t('trainings.content.builder.add_quiz'),
                          icon: Question,
                          onClick: () => {
                            onAddContent({
                              contentItem: contentRegistry.createDefaultItem(
                                'quiz',
                                trainingId
                              ),
                            })
                          },
                        },
                      ]}
                    />
                  )}
                  <Dropdown
                    icon={Ellipsis}
                    items={[
                      {
                        icon: Delete,
                        label: i18n.t('common.delete'),
                        critical: true,
                        onClick() {
                          if (selectedItem) {
                            setShowDeleteModal(true)
                          }
                        },
                      },
                    ]}
                  />
                </Box>
              </Box>
              <Box flexGrow overflowY="auto" width="full">
                <RenderItem item={selectedItem} onContentChange={onContentChange} />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <NavConfirmModal />

      {showDeleteModal && selectedItem && (
        <Dialog
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          header={{
            title: i18n.t('trainings.content.builder.delete.title'),
            description: i18n.t('trainings.content.builder.delete.description'),
            type: 'critical',
          }}
          actions={{
            secondary: {
              label: i18n.t('common.cancel'),
              onClick: () => {
                setShowDeleteModal(false)
              },
            },
            primary: {
              variant: 'critical',
              label: i18n.t('common.remove'),
              onClick: () => {
                ;(onDeleteContent(selectedItem.uuid), setShowDeleteModal(false))
              },
            },
          }}
        />
      )}

      {(hasUnsavedChanges || isThinking) && (
        <F0ActionBar
          isOpen
          primaryActions={[
            {
              label: i18n.t('trainings.content.builder.save'),
              onClick: onSave,
              disabled: isThinking,
            },
          ]}
          secondaryActions={[
            {
              label: i18n.t('trainings.content.builder.discard'),
              onClick: onDiscard,
              disabled: isThinking,
            },
          ]}
          label={
            isThinking
              ? i18n.t('f0.ai.thinking')
              : i18n.t('trainings.content.builder.unsaved_changes')
          }
        />
      )}
    </>
  )
}

function ItemTag({ item }: { item: ContentItem }) {
  const icon = contentRegistry.fetch(item, 'icon')
  const label = contentRegistry.fetch(item, 'label')

  if (!icon || !label) return null

  return <F0TagRaw icon={icon} text={label()} />
}

function RenderItem({ item, onContentChange }: { item: ContentItem; onContentChange: () => void }) {
  const EditComponent = contentRegistry.fetch(item, 'editComponent')
  const fullWidthPreview = contentRegistry.fetch(item, 'fullWidthPreview')

  if (!EditComponent) return null

  const containerStyle = fullWidthPreview
    ? { width: '100%', height: '100%', margin: '0 auto' }
    : { maxWidth: '824px', width: '100%', margin: '0 auto' }

  return (
    <Box style={containerStyle}>
      <EditComponent contentItem={item} onContentChange={onContentChange} />
    </Box>
  )
}

export default ContentBuilder
